import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TYPES_EQUIPEMENT } from '@eec/contrats';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
interface Rapport {
  total: number;
  valide: boolean;
  erreurs: { enregistrement: number; messages: string[] }[];
}
export function ImportPatrimoine() {
  const client = useQueryClient();
  const [csv, setCsv] = useState('');
  const [rapport, setRapport] = useState<Rapport | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  async function verifier() {
    setOccupe(true);
    setErreur('');
    setSucces('');
    setRapport(null);
    try {
      setRapport(
        await requete<Rapport>('/patrimoine/import/verifier', { methode: 'POST', corps: { csv } }),
      );
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  }
  async function importer() {
    setOccupe(true);
    setErreur('');
    try {
      const r = await requete<{ nombre: number }>('/patrimoine/import/appliquer', {
        methode: 'POST',
        corps: { csv },
      });
      setSucces(`${r.nombre} équipement(s) ajouté(s).`);
      setRapport(null);
      setCsv('');
      await client.invalidateQueries({ queryKey: ['patrimoine'] });
    } catch (e) {
      setErreur(messageErreur(e));
      setRapport(null);
    } finally {
      setOccupe(false);
    }
  }
  return (
    <details style={{ marginBlock: 20, minWidth: 0, overflowWrap: 'anywhere' }}>
      <summary>Importer des équipements</summary>
      <p>
        Import CSV UTF-8, séparateur point-virgule, 100 équipements et 50 000 caractères maximum.
        Vérifiez le lot avant de confirmer son ajout. Aucun équipement existant ne sera remplacé.
        Une seule erreur bloque le lot entier.
      </p>
      <p>
        Colonnes dans cet ordre : code;nom;type;province;localisation;latitude;longitude;criticite
      </p>
      <p>
        Province : G1 à G9 ; criticité : 1 à 4. Coordonnées obligatoires, décimales avec point ou
        virgule. Les nouveaux équipements sont enregistrés « En service » ; utilisez la fiche pour
        les compléter.
      </p>
      <details>
        <summary>Types et provinces acceptés</summary>
        <ul>
          {TYPES_EQUIPEMENT.map((t) => (
            <li key={t.code}>
              {t.code} : {t.libelle}
            </li>
          ))}
        </ul>
        <p>
          G1 Estuaire · G2 Haut-Ogooué · G3 Moyen-Ogooué · G4 Ngounié · G5 Nyanga · G6 Ogooué-Ivindo
          · G7 Ogooué-Lolo · G8 Ogooué-Maritime · G9 Woleu-Ntem.
        </p>
      </details>
      <label>
        Fichier CSV{' '}
        <input
          type="file"
          style={{ maxWidth: '100%', boxSizing: 'border-box' }}
          accept=".csv,text/csv"
          disabled={occupe}
          onChange={async (e) => {
            setRapport(null);
            setErreur('');
            setSucces('');
            setCsv('');
            const f = e.target.files?.[0];
            if (!f) return;
            if (f.size > 200000) {
              setErreur('Fichier trop volumineux.');
              return;
            }
            setOccupe(true);
            try {
              const t = await f.text();
              if (t.length > 50000) {
                setErreur('Le fichier dépasse 50 000 caractères.');
                return;
              }
              setCsv(t);
            } catch {
              setErreur('Lecture du fichier impossible.');
            } finally {
              setOccupe(false);
            }
          }}
        />
      </label>
      <label style={{ display: 'block', marginBlock: 12 }}>
        Contenu CSV à vérifier{' '}
        <textarea
          rows={7}
          value={csv}
          maxLength={50000}
          disabled={occupe}
          style={{ width: '100%', maxWidth: 850, boxSizing: 'border-box' }}
          onChange={(e) => {
            setCsv(e.target.value);
            setRapport(null);
            setErreur('');
            setSucces('');
          }}
        />
      </label>
      <button disabled={occupe || !csv} onClick={() => void verifier()}>
        Vérifier le fichier
      </button>
      {erreur && <p role="alert">{erreur}</p>}
      {succes && <p role="status">{succes}</p>}
      {rapport && (
        <div>
          <p role="status">
            {rapport.total} équipement(s) analysé(s).{' '}
            {rapport.valide
              ? 'Lot valide, prêt à être ajouté.'
              : 'Corrigez les erreurs avant tout ajout.'}
          </p>
          {rapport.erreurs.map((r) => (
            <p key={r.enregistrement}>
              Enregistrement {r.enregistrement} : {r.messages.join(' ')}
            </p>
          ))}
          {rapport.valide && (
            <button disabled={occupe} onClick={() => void importer()}>
              Confirmer l’ajout des {rapport.total} équipements
            </button>
          )}
        </div>
      )}
    </details>
  );
}
